import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useDatasetStore, effectiveDashboardName } from '@/store/dataset-store';
import { useFiltersStore } from '@/store/filters-store';
import { sanitizeFileName } from '@/lib/sanitize-filename';
import type { FilterExpression } from '@/types/query';
import { listCharts } from './chart-registry';
import { listCompareTables } from './compare-table-registry';
import type { CompareDeltaRow } from '@/engine/pivots/compare';
// Roboto TTFs ship as committed binary assets (Phase 10 step 4) — see
// `src/assets/fonts/README.md`. Vite resolves these `?url` imports to a
// hashed same-origin asset URL; the actual font bytes are fetched only
// when the user clicks "Export to PDF" so they stay out of the main
// bundle.
import robotoRegularUrl from '@/assets/fonts/Roboto-Regular.ttf?url';
import robotoBoldUrl from '@/assets/fonts/Roboto_Condensed-Bold.ttf?url';

/**
 * Whole-dashboard PDF export. Lazy-loads `jspdf` (D-021) so it stays out
 * of the initial bundle. Layout (D-024): A4 landscape, page 1 = cover
 * with dataset name + active filter chips, then one chart per page.
 *
 * Charts are snapshotted via ECharts `getDataURL({ pixelRatio: 1.5 })` —
 * the same path as `ChartExportButton`, but downscaled relative to the
 * standalone PNG (2×) so a 12-widget dashboard doesn't produce a 30 MB
 * PDF. `html2canvas` is forbidden by the brief.
 */
const PIXEL_RATIO = 1.5;
const A4_LANDSCAPE_W_MM = 297;
const A4_LANDSCAPE_H_MM = 210;
const PAGE_PADDING_MM = 12;

export function DashboardPdfButton(): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const activeId = useDatasetStore((s) => s.activeDatasetId);
  const datasets = useDatasetStore((s) => s.datasets);
  const filters = useFiltersStore((s) => s.filters);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const entry = activeId ? datasets.get(activeId) : undefined;
  if (!entry || !activeId) return null;

  async function handleClick(): Promise<void> {
    if (!entry || !activeId) return;
    setError(null);
    const charts = listCharts();
    const compareTables = listCompareTables();
    if (charts.length === 0 && compareTables.length === 0) {
      setError(t('export.pdf.noCharts'));
      return;
    }
    setBusy(true);
    try {
      const bg = readThemeBackground();
      const fg = readThemeForeground();

      // Snapshot every chart up-front so the PDF library doesn't need to
      // wait on the DOM between addImage calls.
      const snapshots = charts.flatMap((c) => {
        const inst = c.getInstance();
        if (!inst) return [];
        const dataUrl = inst.getDataURL({
          type: 'png',
          pixelRatio: PIXEL_RATIO,
          backgroundColor: bg,
        });
        const opt = inst.getOption() as { width?: number; height?: number };
        const w = opt.width ?? inst.getWidth();
        const h = opt.height ?? inst.getHeight();
        return [{ title: c.title, dataUrl, w, h }];
      });

      const compareTableSnaps = compareTables.flatMap((c) => {
        const snap = c.getSnapshot();
        // Skip widgets that haven't resolved yet — same policy as
        // chart snapshots above, so the export doesn't emit empty
        // pages for half-loaded data.
        return snap ? [{ title: c.title, ...snap }] : [];
      });

      if (snapshots.length === 0 && compareTableSnaps.length === 0) {
        setError(t('export.pdf.notReady'));
        return;
      }

      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      });

      // Register Roboto before any text() call so the cover heading +
      // every chart title can render Turkish glyphs. Built-in helvetica
      // would silently drop ş/ğ/ı/İ/ç/Ö/Ü on a TR-locale dashboard.
      await registerRobotoFonts(doc);

      drawCoverPage(doc, {
        // Cover page shows the user-chosen dashboard label so a re-imported
        // file with a custom name reads sensibly. Falls back to the file
        // basename via effectiveDashboardName.
        fileName: effectiveDashboardName(entry),
        rowCount: entry.rowCount,
        chartCount: snapshots.length + compareTableSnaps.length,
        filters,
        bg,
        fg,
        t,
        dateLocale: i18n.language === 'tr' ? 'tr-TR' : 'en-US',
      });

      for (const snap of snapshots) {
        doc.addPage();
        drawChartPage(doc, snap, { bg, fg });
      }

      for (const cts of compareTableSnaps) {
        doc.addPage();
        drawCompareTablePage(doc, cts, {
          bg,
          fg,
          winnersHeader: t('compare.table.winnersHeader', { n: cts.limit }),
          losersHeader: t('compare.table.losersHeader', { n: cts.limit }),
          categoryCol: t('compare.table.categoryCol'),
          diffCol: t('compare.table.diffCol'),
          percentCol: t('compare.table.percentCol'),
          noRows: t('common.noData'),
        });
      }

      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const base = sanitizeFileName(effectiveDashboardName(entry)) || 'dashboard';
      doc.save(`${base}-${stamp}.pdf`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('export.unknownError');
      setError(t('export.pdf.failed', { message: msg }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => void handleClick()}
        disabled={busy}
        className="w-full text-xs px-2.5 py-2 rounded-md hover:opacity-80 text-left disabled:opacity-50"
        style={{
          color: 'var(--color-fg)',
          background: 'var(--color-hover)',
          border: '1px solid var(--color-border)',
        }}
      >
        {busy ? t('export.pdf.busy') : t('export.pdf.label')}
      </button>
      {error && (
        <span
          className="text-[11px] px-1"
          style={{ color: 'var(--color-danger, #ef4444)' }}
          role="alert"
        >
          {error}
        </span>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// PDF rendering
// ---------------------------------------------------------------------------

type CoverInput = {
  fileName: string;
  rowCount: number;
  chartCount: number;
  filters: FilterExpression[];
  bg: string;
  fg: string;
  t: TFunction;
  /** BCP-47 tag for `Date.toLocaleString`, derived from the active i18n locale. */
  dateLocale: string;
};

type ChartSnapshot = {
  title: string;
  dataUrl: string;
  w: number;
  h: number;
};

type Theme = { bg: string; fg: string };

// Use loose typing for the jspdf instance to avoid a dynamic-import-only
// Jspdf type leaking into our hot path. The methods we call are stable.
type Doc = {
  setFillColor: (r: number, g: number, b: number) => Doc;
  rect: (x: number, y: number, w: number, h: number, style: string) => Doc;
  setTextColor: (r: number, g: number, b: number) => Doc;
  setFontSize: (n: number) => Doc;
  setFont: (family: string, style?: string) => Doc;
  text: (
    text: string | string[],
    x: number,
    y: number,
    opts?: { align?: 'left' | 'center' | 'right' },
  ) => Doc;
  /** Set draw colour used by `rect()` outlines. */
  setDrawColor: (r: number, g: number, b: number) => Doc;
  /** Pen width (mm) for the stroke style used by `rect()` outlines. */
  setLineWidth: (w: number) => Doc;
  splitTextToSize: (text: string, maxWidth: number) => string[];
  addImage: (
    data: string,
    fmt: string,
    x: number,
    y: number,
    w: number,
    h: number,
  ) => Doc;
  /** Register a binary file (e.g. TTF) under the jsPDF virtual file system. */
  addFileToVFS: (filename: string, base64: string) => void;
  /** Register a VFS-stored TTF as a usable jsPDF font family + style. */
  addFont: (filename: string, family: string, style: string) => void;
  internal: { pageSize: { getWidth(): number; getHeight(): number } };
};

/**
 * Fetch the bundled Roboto TTFs and register them with jsPDF so the
 * cover page + chart titles can render Turkish glyphs (ş/ğ/ı/İ/ç/Ö/Ü).
 * jsPDF's built-in `helvetica` is a Type 1 font without those glyphs;
 * setting `font = 'Roboto'` after this call switches all subsequent
 * `text()` invocations onto the embedded TrueType face.
 *
 * Both fonts are fetched from the same-origin Vite-bundled asset URLs
 * — no external network roundtrip. The ~320 kB of TTF bytes only
 * loads when the user clicks "Export to PDF" so they stay out of the
 * main bundle.
 */
async function registerRobotoFonts(doc: Doc): Promise<void> {
  const [regular, bold] = await Promise.all([
    fetchTtfAsBase64(robotoRegularUrl),
    fetchTtfAsBase64(robotoBoldUrl),
  ]);
  doc.addFileToVFS('Roboto-Regular.ttf', regular);
  doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
  doc.addFileToVFS('Roboto-Bold.ttf', bold);
  doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');
}

async function fetchTtfAsBase64(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to load PDF font ${url}: ${res.status}`);
  }
  const buf = await res.arrayBuffer();
  // Chunked binary → ASCII so we don't blow the call stack on a 160 kB
  // file. 32 kB chunks keep `String.fromCharCode(...args)` within the
  // engine's apply-arg limit.
  const bytes = new Uint8Array(buf);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + chunkSize)),
    );
  }
  return btoa(binary);
}

function drawCoverPage(doc: Doc, input: CoverInput): void {
  const { fileName, rowCount, chartCount, filters, bg, fg, t, dateLocale } =
    input;
  paintBackground(doc, bg);
  const fgRgb = hexToRgb(fg);
  doc.setTextColor(fgRgb.r, fgRgb.g, fgRgb.b);

  const pageW = doc.internal.pageSize.getWidth();
  let y = PAGE_PADDING_MM + 8;

  doc.setFontSize(22).setFont('Roboto', 'bold');
  doc.text(t('export.pdf.cover.title'), PAGE_PADDING_MM, y);
  y += 12;

  doc.setFontSize(11).setFont('Roboto', 'normal');
  doc.text(
    t('export.pdf.cover.source', { name: fileName }),
    PAGE_PADDING_MM,
    y,
  );
  y += 6;
  doc.text(
    t('export.pdf.cover.summary', {
      rows: rowCount.toLocaleString(),
      charts: chartCount,
    }),
    PAGE_PADDING_MM,
    y,
  );
  y += 6;
  doc.text(
    t('export.pdf.cover.generated', {
      datetime: new Date().toLocaleString(dateLocale),
    }),
    PAGE_PADDING_MM,
    y,
  );
  y += 12;

  doc.setFontSize(13).setFont('Roboto', 'bold');
  doc.text(t('export.pdf.cover.filtersHeader'), PAGE_PADDING_MM, y);
  y += 8;

  doc.setFontSize(10).setFont('Roboto', 'normal');
  if (filters.length === 0) {
    doc.text(t('export.pdf.cover.noFilters'), PAGE_PADDING_MM, y);
  } else {
    const maxWidth = pageW - PAGE_PADDING_MM * 2;
    for (const f of filters) {
      const line = formatFilter(f);
      const wrapped = doc.splitTextToSize(`• ${line}`, maxWidth);
      doc.text(wrapped, PAGE_PADDING_MM, y);
      y += 5 * wrapped.length;
      if (y > A4_LANDSCAPE_H_MM - PAGE_PADDING_MM) break;
    }
  }
}

function drawChartPage(
  doc: Doc,
  snap: ChartSnapshot,
  theme: Theme,
): void {
  paintBackground(doc, theme.bg);
  const fgRgb = hexToRgb(theme.fg);
  doc.setTextColor(fgRgb.r, fgRgb.g, fgRgb.b);

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  doc.setFontSize(14).setFont('Roboto', 'bold');
  const titleLines = doc.splitTextToSize(
    snap.title,
    pageW - PAGE_PADDING_MM * 2,
  );
  doc.text(titleLines, PAGE_PADDING_MM, PAGE_PADDING_MM + 4);

  const titleHeight = titleLines.length * 6;
  const top = PAGE_PADDING_MM + titleHeight + 4;
  const availW = pageW - PAGE_PADDING_MM * 2;
  const availH = pageH - top - PAGE_PADDING_MM;
  const aspect = snap.w > 0 && snap.h > 0 ? snap.w / snap.h : 16 / 9;

  let drawW = availW;
  let drawH = availW / aspect;
  if (drawH > availH) {
    drawH = availH;
    drawW = availH * aspect;
  }
  const x = PAGE_PADDING_MM + (availW - drawW) / 2;
  const y = top + (availH - drawH) / 2;
  doc.addImage(snap.dataUrl, 'PNG', x, y, drawW, drawH);
}

// ---------------------------------------------------------------------------
// Compare-table PDF page
// ---------------------------------------------------------------------------

type CompareTablePageInput = {
  title: string;
  winners: CompareDeltaRow[];
  losers: CompareDeltaRow[];
  labelA: string;
  labelB: string;
};

type CompareTableTheme = {
  bg: string;
  fg: string;
  winnersHeader: string;
  losersHeader: string;
  categoryCol: string;
  diffCol: string;
  percentCol: string;
  noRows: string;
};

/** Column widths in mm for a single compare-table sub-table (sum = 130mm). */
const CT_SUB_COL_WIDTHS_MM = [45, 25, 25, 20, 15] as const;
const CT_SUB_TABLE_W_MM = CT_SUB_COL_WIDTHS_MM.reduce((s, w) => s + w, 0);
const CT_ROW_H_MM = 6;
const CT_SUB_GAP_MM = 13;

/**
 * Render a compare-table widget on its own A4-landscape page using
 * native jsPDF primitives — `text()` for content and `rect()` for cell
 * borders so it actually looks like a table. html2canvas is forbidden
 * by the brief, and an ECharts snapshot doesn't apply here (the source
 * is HTML), so the page is composed cell-by-cell.
 *
 * Layout: winners on the left, losers on the right, matching the
 * side-by-side Excel block. Both sub-tables share the column schema
 * `[category, period A, period B, delta, percent]` and use the
 * widget's localized headers so the export matches the live UI.
 */
function drawCompareTablePage(
  doc: Doc,
  input: CompareTablePageInput,
  theme: CompareTableTheme,
): void {
  paintBackground(doc, theme.bg);
  const fgRgb = hexToRgb(theme.fg);
  doc.setTextColor(fgRgb.r, fgRgb.g, fgRgb.b);

  const pageW = doc.internal.pageSize.getWidth();
  let y = PAGE_PADDING_MM + 6;

  doc.setFontSize(14).setFont('Roboto', 'bold');
  const titleLines = doc.splitTextToSize(
    input.title,
    pageW - PAGE_PADDING_MM * 2,
  );
  doc.text(titleLines, PAGE_PADDING_MM, y);
  y += titleLines.length * 6 + 4;

  const colLabels = [
    theme.categoryCol,
    input.labelA,
    input.labelB,
    theme.diffCol,
    theme.percentCol,
  ];

  // Centre the side-by-side pair horizontally on the page so it
  // doesn't hug the left margin when the inner tables don't fill the
  // available width.
  const pairW = CT_SUB_TABLE_W_MM * 2 + CT_SUB_GAP_MM;
  const leftMargin = Math.max(PAGE_PADDING_MM, (pageW - pairW) / 2);
  const winnersX = leftMargin;
  const losersX = leftMargin + CT_SUB_TABLE_W_MM + CT_SUB_GAP_MM;

  // Sub-table headings sit just above each table.
  doc.setFontSize(11).setFont('Roboto', 'bold');
  doc.text(theme.winnersHeader, winnersX, y);
  doc.text(theme.losersHeader, losersX, y);
  y += 5;

  // Both sub-tables share the same vertical span so the page looks
  // balanced; the longer side dictates the row count.
  const rowCount = Math.max(input.winners.length, input.losers.length);

  drawDeltaSubTable(doc, theme, y, winnersX, colLabels, input.winners, rowCount);
  drawDeltaSubTable(doc, theme, y, losersX, colLabels, input.losers, rowCount);
}

/**
 * Draw one delta sub-table starting at `(x, y)`. `rowCount` is the
 * shared row count between the two sub-tables so winners and losers
 * line up vertically when one side has fewer rows than the other.
 */
function drawDeltaSubTable(
  doc: Doc,
  theme: CompareTableTheme,
  startY: number,
  x: number,
  columnLabels: readonly string[],
  rows: readonly CompareDeltaRow[],
  rowCount: number,
): void {
  let y = startY;

  // Header row.
  doc.setFontSize(10).setFont('Roboto', 'bold');
  drawCompareTableRow(
    doc,
    x,
    y,
    columnLabels,
    ['left', 'right', 'right', 'right', 'right'],
    true,
  );
  y += CT_ROW_H_MM;

  if (rowCount === 0) {
    drawCompareTableRow(
      doc,
      x,
      y,
      [theme.noRows, '', '', '', ''],
      ['left', 'right', 'right', 'right', 'right'],
      false,
    );
    return;
  }

  doc.setFont('Roboto', 'normal');
  for (let i = 0; i < rowCount; i++) {
    const r = rows[i];
    const cells = r
      ? [
          r.category,
          formatPdfNumber(r.valueA),
          formatPdfNumber(r.valueB),
          formatPdfDelta(r.delta),
          formatPdfPercent(r.percent),
        ]
      : ['', '', '', '', ''];
    drawCompareTableRow(
      doc,
      x,
      y,
      cells,
      ['left', 'right', 'right', 'right', 'right'],
      false,
    );
    y += CT_ROW_H_MM;
  }
}

/**
 * Draw a single table row at `(x, y)`. Each cell is outlined with
 * `rect()` so the result reads as a bordered table rather than free
 * floating text. Header rows get a light grey fill behind the text.
 */
function drawCompareTableRow(
  doc: Doc,
  x: number,
  y: number,
  cells: readonly string[],
  aligns: readonly ('left' | 'right')[],
  isHeader: boolean,
): void {
  let cx = x;
  for (let i = 0; i < cells.length; i++) {
    const w = CT_SUB_COL_WIDTHS_MM[i] ?? 25;
    // Cell background + border. Header rows get a yellow fill so they
    // stand out the same way the Excel header cells do (item 2 carry).
    if (isHeader) doc.setFillColor(255, 235, 110);
    else doc.setFillColor(255, 255, 255);
    doc.rect(cx, y, w, CT_ROW_H_MM, isHeader ? 'FD' : 'S');

    const text = cells[i] ?? '';
    const fitted = doc.splitTextToSize(text, w - 2)[0] ?? '';
    if (aligns[i] === 'right') {
      doc.text(fitted, cx + w - 2, y + 4, { align: 'right' });
    } else {
      doc.text(fitted, cx + 2, y + 4);
    }
    cx += w;
  }
}

function formatPdfNumber(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('tr-TR');
}

function formatPdfDelta(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n).toLocaleString('tr-TR');
  return n >= 0 ? `+${abs}` : `-${abs}`;
}

function formatPdfPercent(p: number | null): string {
  if (p == null) return '—';
  const v = (p * 100).toFixed(1);
  return p >= 0 ? `+${v}%` : `${v}%`;
}

function paintBackground(doc: Doc, bg: string): void {
  const { r, g, b } = hexToRgb(bg);
  doc.setFillColor(r, g, b);
  doc.rect(0, 0, A4_LANDSCAPE_W_MM, A4_LANDSCAPE_H_MM, 'F');
}

function formatFilter(f: FilterExpression): string {
  const v = Array.isArray(f.value)
    ? f.value.map(String).join(', ')
    : String(f.value);
  return `${f.column} ${f.operator} ${v}`;
}

// ---------------------------------------------------------------------------
// Theme color helpers (CSS variable readback at click time)
// ---------------------------------------------------------------------------

function readThemeBackground(): string {
  return readCssVar('--color-bg', '#ffffff');
}

function readThemeForeground(): string {
  return readCssVar('--color-fg', '#111111');
}

function readCssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return v.length > 0 ? v : fallback;
}

/**
 * Convert a CSS color (#rgb, #rrggbb, or rgb(r, g, b)) to a numeric triple.
 * Returns black on parse failure — better than throwing inside the export
 * happy path.
 */
function hexToRgb(input: string): { r: number; g: number; b: number } {
  const s = input.trim();
  if (s.startsWith('#')) {
    const hex = s.slice(1);
    if (hex.length === 3) {
      return {
        r: parseInt(hex[0]! + hex[0]!, 16),
        g: parseInt(hex[1]! + hex[1]!, 16),
        b: parseInt(hex[2]! + hex[2]!, 16),
      };
    }
    if (hex.length === 6) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }
  }
  const rgb = s.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgb) {
    return {
      r: Number(rgb[1]),
      g: Number(rgb[2]),
      b: Number(rgb[3]),
    };
  }
  return { r: 0, g: 0, b: 0 };
}
